import {Component, ElementRef, HostListener, inject, ViewChild} from '@angular/core';
import {Router} from "@angular/router";
import {AppRoutes} from "../../../../../enums/routes";
import {AnalyticsRouting} from "../../../common/routes/analytics-routing";
import {NgSelectComponent} from "@ng-select/ng-select";
import {FormsModule} from "@angular/forms";
import {CompanyTimeZoneService} from "../../../../../_shared/time/company-time-zone.service";
import {
  addCalendarDays,
  companyDayStartInstant,
  companyToday,
  DayKey,
  dayKeyOf,
  isDayKey,
  parseDayKey,
  startOfMonth
} from "../../../../../_shared/time/company-day";

/**
 * Custom-range → server instants (#657).
 *
 * `Analysis/EventAnalysis/FetchEventData` returns the events whose START lies in `[startTime, endTime]`,
 * `x.Start >= startTime && x.Start <= endTime`
 * (`API.Infrastructure/Implementations/Database/Repositories/Specializations/Shard/EventAnalysisRepository.cs`,
 * `GetEvents`) — BOTH bounds INCLUSIVE, and an event belongs to the range its start falls in (a night event starting
 * on 31.08. counts for 31.08. even though it ends on 01.09.). So "31.08." meaning the whole 31.08. requires `endTime`
 * to be the LAST instant of that company day: the next company-local midnight minus one second.
 */
export function analysisRangeInstants(from: DayKey, to: DayKey, timeZone: string): { start: Date; end: Date } {
  const start = companyDayStartInstant(from, timeZone);
  const exclusiveEnd = companyDayStartInstant(addCalendarDays(to, 1), timeZone);
  return {start, end: new Date(exclusiveEnd.getTime() - 1000)};
}

@Component({
  selector: 'app-time-selector-view',
  templateUrl: './time-selector-view.component.html',
  imports: [
    NgSelectComponent,
    FormsModule
  ],
  styleUrls: ['./time-selector-view.component.css']
})
export class TimeSelectorViewComponent {
  @ViewChild('startTimePicker', {static: false}) startDatePicker?: ElementRef<HTMLInputElement>;
  @ViewChild('endTimePicker', {static: false}) endDatePicker?: ElementRef<HTMLInputElement>;

  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  public timePeriods: any[] = [
    {id: 0, name: 'Laufender Monat'},
    {id: 1, name: 'Laufendes Jahr'},
    {id: 2, name: 'Letzte 30 Tage'},
    {id: 3, name: 'Letzte 90 Tage'},
    {id: 4, name: 'Letzte 365 Tage'},
    {id: 5, name: 'Benutzerdefiniert'}
  ];
  public selectedTimePeriod: any = this.timePeriods[5];
  public selectedTimePeriodId: number = this.selectedTimePeriod.id;

  /**
   * The `<input type="date">` values — a native date input's own value IS a `yyyy-MM-dd` day key already, so
   * these are used verbatim as company day keys; no `Date` ever stands in for one (#657, the original bug: a
   * `new Date("yyyy-MM-dd")` parses as UTC midnight, dropping the whole end day).
   */
  public visibleEndDate: DayKey | '' = '';
  public visibleStartDate: DayKey | '' = '';
  private runBtn?: ElementRef<HTMLButtonElement>;

  /**
   * True once the company zone has answered and the default range is filled in. Before that the dates are empty
   * and "Analysieren" is disabled: computing "today" with the Europe/Berlin fallback would pre-fill TOMORROW's
   * date for a UTC or New York company in the hours after Berlin midnight.
   */
  protected ready = false;

  /** The zone `load()` answered with; every calendar computation below uses exactly this one. */
  private loadedZone: string | undefined;

  constructor(private router: Router) {
    this.companyTimeZone.load().subscribe(timeZone => {
      const today = companyToday(timeZone);
      this.loadedZone = timeZone;
      this.ready = true;
      if (this.selectedTimePeriodId != 5) {
        // A preset picked while the zone was loading: recompute it in the company's zone.
        this.setTimerange(this.selectedTimePeriodId);
        return;
      }
      // Keep anything the user already picked; fill only what is still empty.
      if (!isDayKey(this.visibleStartDate)) this.visibleStartDate = today;
      if (!isDayKey(this.visibleEndDate)) this.visibleEndDate = today;
    });
  }

  @ViewChild('runBtn', {static: false}) set content(content: ElementRef<HTMLButtonElement>) {
    this.runBtn = content;
  }

  private get timeZone(): string {
    return this.loadedZone ?? this.companyTimeZone.timeZone();
  }

  setDateToCurrentMonth() {
    const today = companyToday(this.timeZone);
    this.visibleEndDate = today;
    this.visibleStartDate = startOfMonth(today);
  }

  setDateToCurrentYear() {
    const today = companyToday(this.timeZone);
    this.visibleEndDate = today;
    this.visibleStartDate = dayKeyOf(parseDayKey(today).year, 1, 1);
  }

  setDateToLast30Days() {
    const today = companyToday(this.timeZone);
    this.visibleEndDate = today;
    this.visibleStartDate = addCalendarDays(today, -30);
  }

  setDateToLast90Days() {
    const today = companyToday(this.timeZone);
    this.visibleEndDate = today;
    this.visibleStartDate = addCalendarDays(today, -90);
  }

  setDateToLast365Days() {
    const today = companyToday(this.timeZone);
    this.visibleEndDate = today;
    this.visibleStartDate = addCalendarDays(today, -365);
  }

  navigateToAnalyses() {
    if (!this.ready) return;
    if (this.selectedTimePeriodId == 5) {
      // A native date input's raw value is already a "yyyy-MM-dd" day key — read it directly, no `new Date(...)`.
      const startValue = this.startDatePicker?.nativeElement.value;
      const endValue = this.endDatePicker?.nativeElement.value;
      if (isDayKey(startValue)) this.visibleStartDate = startValue;
      if (isDayKey(endValue)) this.visibleEndDate = endValue;
    }

    if (!isDayKey(this.visibleStartDate) || !isDayKey(this.visibleEndDate)) return;
    const {start, end} = analysisRangeInstants(this.visibleStartDate, this.visibleEndDate, this.timeZone);
    this.router.navigate(["/" + AppRoutes.Analytics, {outlets: {analytics: [AnalyticsRouting.RevenuePerformanceTracker]}}], {
      queryParams: {
        startTime: Math.floor(start.getTime() / 1000),
        endTime: Math.floor(end.getTime() / 1000)
      }
    })
  }

  setTimerange(id: number) {
    this.selectedTimePeriodId = id;
    // Before the zone is known a preset only records its choice; the load recomputes it (see the constructor).
    if (!this.ready) return;
    switch (id) {
      case 0:
        this.setDateToCurrentMonth();
        break;
      case 1:
        this.setDateToCurrentYear();
        break;
      case 2:
        this.setDateToLast30Days();
        break;
      case 3:
        this.setDateToLast90Days();
        break;
      case 4:
        this.setDateToLast365Days();
        break;
      default:
        break;
    }
  }

  setVisibleDateChanged() {
    this.selectedTimePeriod = this.timePeriods[5];
    this.selectedTimePeriodId = this.selectedTimePeriod.id;
  }


  @HostListener('document:mousemove', ['$event']) onmousemove(event: MouseEvent) {
    //check if mouse is over button
    if (this.runBtn?.nativeElement!.contains(event.target as Node)) {
      this.onMouseOverButton(event);
    }

  }


  onMouseOverButton($event: MouseEvent) {

    let x = $event.clientX - this.runBtn!.nativeElement.getBoundingClientRect().left;

    let y = $event.clientY - this.runBtn!.nativeElement.getBoundingClientRect().top;

    this.runBtn?.nativeElement!.style.setProperty('--x', x + 'px');

    this.runBtn?.nativeElement!.style.setProperty('--y', y + 'px');

  }

}
